/**
 * TODO:
 * 
 * Phase 1:
 * - Allow users to access analysis screen from the nav
 * - Remove the analysis name - by definition they are always ephemeral in phase 1
 * - Allow downloading a CSV of analysis results
 * - Allow searching the table like we do with other screens
 * - Bug: when drilling down, the same filter may be added multiple times
 * - Bug: bar charts are having issues when dates are empty
 * 
 * Phase 2:
 * - Allow users to save analyses
 *      - In production, saving an analysis needs a different button to copy it into DEV
 *      - In DEV, if you are editing an existing analysis, you can either save it or copy it
 *      - If you are drilling down from a "saved" analysis, it will create a new ephemeral analysis to handle the drill-down so you don't affect the structure of the current analysis
 */


import { PipelineNode, PipelineNodeExtraReportColumn, PipelineNodeField, PipelineNodeMeasureJoinTree, PipelineNodeORM, useNodesThatNeedBuilding, useReportingPaths } from "@models/pipelineNode";
import { ReportBuilderDimension, ReportBuilderDimensionJoinTree, ReportBuilderDimensionORM, ReportBuilderMeasure, ReportBuilderMeasureORM, useDimensions, useMeasures, useUniqueValuesInDimension } from "@models/reportBuilder";
import PageStructure, { PageContent, PageContentHeader, PageContentInner, PageSidebar, Pane, PaneContent } from "@pages/PageStructure.component";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Form, Modal, Offcanvas } from "react-bootstrap";
import { Updater, useImmer } from "use-immer";
import { invalidatePipelineNodes, toggleFavoriteAnalysis, useIsFavoriteAnalysis, useIsInDraftMode, usePipelineNode, usePipelineNodes, useReportingTree } from "@stores/data.store";
import Dropdown, { MultiDropdown, Option } from "@components/form/Dropdown.component";
import { graphlib } from "dagre";

import  { getConnectionsForAllGraphNodes } from "@components/datamodel/MeasureForm.component";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled, { css } from "styled-components";

import { reorderList } from "@services/list.service";
import { Analysis, AnalysisDimensionFilter, AnalysisORM, AnalysisSortOrder, AnalysisVisualizationConfig } from "@models/analysis";
import AsyncButton from "@components/button/AsyncButton.component";
import BuildOrchestrationORM, { BuildOrchestration } from "@models/buildOrchestration";
import { getErrorMessage } from "@services/errors.service";
import PliableLoader from "@components/loaders/PliableLoader.component";
import Danger from "@components/statusIndicators/Danger.component";
import Warning from "@components/statusIndicators/Warning.component";
import { Column } from "@components/datatable/DataTable.component";
import Chart from 'react-apexcharts';
import produce from "immer";
import ConfettiExplosion from "react-confetti-explosion";
import DataTablePagination from "@components/datatable/Pagination.component";
import LoadingCard from "@components/card/LoadingCard.component";
import { formatValue } from "@services/formatting.service";
import toast from "@services/toast.service";
import SaveButton from "@components/button/SaveButton.component";
import EditableText from "@components/general/EditableText.component";
import { fastInnerHTML } from "handsontable/helpers/dom";
import { getPromptAnswer, requireConfirmation, runBuild } from "@services/alert/alert.service";
import { useRouteBlocker } from "@services/routing.service";
import { timeAgo } from "@services/time.service";
import { DraftOnly, ProdOnly, ReadOnly } from "@components/project/DraftModeRequired.component";
import { MiddlePanel } from "redoc";

import { useDebounce } from "use-debounce";
import DateRangeSelector from '@components/form/DateRangeSelector.component';
import PipelineNodeSelector from "@components/pipelineNodes/PipelineNodeSelector.component";
import PipelineNodeColumnSelector from "@components/pipelineNodes/PipelineNodeColumnSelector.component";
import BigMetricCard from "@components/metric/BigMetricCard.component";

import { DrillDownParams, BaseVisualizationProps } from "@models/analysisVisualization";
import { DashboardIcon } from "@components/pipelineNodes/PipelineNodeIcon.component";
import { getAnalysisIcon } from "@components/pipelineNodes/PipelineNodeList.component";
import { useQueryParams } from "@services/url.service";
import { getRandomLoadingMessage } from "@services/alert/BuildModal.component";
import FavoriteButton from "@components/button/FavoriteButton.component";
import ApiService, { JobEnqueueResponse, SingleRecordResponse } from "@services/api/api.service";
import InfoAlert from "@components/statusIndicators/InfoAlert.component";
import { useFeatureFlag } from "@services/featureFlags/featureFlag.service";
import BackgroundService from "@services/bg.service";
import SingleDimensionFilter from "@pages/Analysis/SingleDimensionFilter.component";
import { LTVPivotTableForm, LTVPivotTableVisualization } from "@pages/Analysis/premium/LTVPivotTable";
import { CustomerAcquisitionForm, CustomerAcquisitionVisualization } from "@pages/Analysis/premium/CustomerAcquisition";
import MetricSelector from "@pages/Analysis/MetricSelector.component";
import DimensionSelector from "@pages/Analysis/DimensionSelector.component";
import HumanReadableDimensionFilters from "@pages/Analysis/HumanReadableDimensionFilters.component";

interface VisualizationProps<T> extends BaseVisualizationProps<T> {}

interface AssistantResponse {
    analysis: Analysis;
    explanation: string;
}

export interface TableVisualizationProps {
    options: {
        columns: {
            field: string;
            entityType: string;
            entityIdx: number;
            headerName: string;
            sortable: boolean;
            filter: boolean;
            formatter: string;
        }[];
        data: Record<string, any>[];
        total_records: number;
    }
}


interface SortConfigurationProps {
    sortOrder: AnalysisSortOrder[];
    params: TableVisualizationProps;
    measureIds: string[];
    dimensionIds: string[];
    onRemoveSort?: (entityType: string, entityId: string) => void;
}

const SortConfiguration = (props: SortConfigurationProps) => {
    const activeSorts = useMemo(() => {
        return props.sortOrder.map(sort => {
            const column = props.params.options.columns.find(c => {
                if (sort.entity_type === c.entityType && 
                    ((c.entityType === 'METRIC' && props.measureIds[c.entityIdx] === sort.entity_id) ||
                     (c.entityType === 'DIMENSION' && props.dimensionIds[c.entityIdx] === sort.entity_id))) {
                    return true;
                }
                return false;
            });
            return {
                columnName: column?.headerName || 'Unknown',
                direction: sort.ascending ? 'ascending' : 'descending',
                entityType: sort.entity_type,
                entityId: sort.entity_id
            };
        });
    }, [props.sortOrder, props.params.options.columns, props.measureIds, props.dimensionIds]);

    if (activeSorts.length === 0) {
        return null;
    }

    return (
        <div>
            <small className="text-muted">
                Sorted by: {activeSorts.map((sort, idx) => (
                    <Badge key={idx} className="me-1">
                        <strong>{sort.columnName}</strong> ({sort.direction})
                        {props.onRemoveSort && (
                            <a
                                role="button"
                                className="ms-1"
                                onClick={() => props.onRemoveSort!(sort.entityType, sort.entityId)}
                                style={{ color: 'inherit' }}
                            >
                                <i className="mdi mdi-close-circle" />
                            </a>
                        )}
                    </Badge>
                ))}
            </small>
        </div>
    );
}

export interface SingleValueVisualizationProps {
    options: {
        value: number | null;
        format: string | null;  // Formatter type (e.g., 'NUMERIC', 'CURRENCY', etc.)
        name: string;  // Display name/label for the value
        previous_value: number | null;
    }
}


export const SingleValueVisualization = (props: VisualizationProps<SingleValueVisualizationProps>) => {
    return <div>
        <BigMetricCard
            title={props.params.options.name}
            metric={formatValue({
                value: props.params.options.value,
                format: props.params.options.format || ''
            })}
            pctChange={(props.params.options.previous_value && props.params.options.value) ? (props.params.options.value - props.params.options.previous_value) / props.params.options.previous_value : undefined}
            loading={false}
            icon=""
        />

    </div>
}

export const TableVisualization = (props: VisualizationProps<TableVisualizationProps>) => {
    const dimColumns = useMemo(() => {
        return props.params.options.columns.filter(c => c.entityType == 'DIMENSION').map(c => c.field);
    }, [props.params.options.columns]);

    const onClickMetric = useCallback((metricIdx: number, rowData: Record<string, any>) => {
        const dimensionValues = dimColumns.map(d => rowData[d]);
        const dimensionFilters = dimensionValues.map((val, idx) => ({
            dimension_id: props.dimensionIds[idx],
            comparator: (!val || val === '(EMPTY)') ? 'EMPTY' : 'IN',
            value: (!val || val === '(EMPTY)') ? '' : [val],
        }));

        props.onDrillDown({
            metricIds: [props.measureIds[metricIdx]],
            dimensionIds: props.dimensionIds,
            dimensionFilters
        });
    }, [props.onDrillDown, dimColumns, props.dimensionIds, props.measureIds]);

    const columns = useMemo(() => {
        return props.params.options.columns.map(c => {
            const rv: Column = {
                header: c.headerName,
                key: c.field,
                onClick: undefined,
            }

            if (c.entityType == 'METRIC' && c.entityIdx >= 0) {
                rv.onClick = (value, rowValues) => {
                    onClickMetric(c.entityIdx, rowValues);
                }
            }
            return rv;
        });
    }, [props.params.options.columns]);

    const sortDirByEntity = useMemo(() => {
        const sortDirs: {[key: string]: string} = {};
        props.sortOrder.forEach(s => {
            sortDirs[s.entity_type + ':' + s.entity_id] = s.ascending ? 'asc' : 'desc';
        });
        return sortDirs;
    }, [props.sortOrder]);

    const currentPage = useMemo(() => {
        return props.currentPage || 1;
    }, [props.currentPage]);

    const totalPages = useMemo(() => {
        return Math.ceil(props.params.options.total_records / (props.pageSize || 50));
    }, [props.params.options.total_records, props.pageSize]);

    return <div className="h-100 d-flex flex-vertical">
        
        <div style={{ position: 'relative', overflow: 'auto', flex: 1 }}>
            <table className="table table-bordered table-compact table-sticky table-hover">
                <thead className="bg-light">
                    <tr>
                        {props.params.options.columns.map(c => {
                            const entityType = c.entityType;
                            let entityId = '';
                            if (entityType == 'METRIC') {
                                entityId = props.measureIds[c.entityIdx];
                            } else if (entityType == 'DIMENSION') {
                                entityId = props.dimensionIds[c.entityIdx];
                            }
                            const sortDir = sortDirByEntity[entityType + ':' + entityId];

                            return <th key={c.field}>
                                <div className="d-flex center-vertically">
                                    <div className="overflow-ellipsis" style={{ maxWidth: '30vw' }} title={c.headerName}>
                                        {c.headerName}
                                    </div>
                                    <div className="flex-1"></div>
                                    {c.sortable !== false && <div>
                                        <a role="button" onClick={() => {
                                            props.onToggleSort(entityType, entityId);
                                        }}>
                                            {sortDir == 'asc' && <i className="mdi mdi-sort-ascending"></i>}
                                            {sortDir == 'desc' && <i className="mdi mdi-sort-descending"></i>}
                                            {!sortDir && <i className="mdi mdi-sort"></i>}
                                        </a>
                                    </div>}
                                </div>
                            </th>;
                        })}
                    </tr>
                </thead>
                <tbody>
                    {props.params.options.data.map((row, idx) => (
                        <tr key={idx}>
                            {props.params.options.columns.map(c => {
                                if (c.entityType == 'DIMENSION') {
                                    return <th key={c.field}>{row[c.field]}</th>
                                }
                                return <td key={c.field}>
                                    <a role="button" className="force-link" onClick={() => {
                                        onClickMetric(c.entityIdx, row);
                                    }}>{formatValue({
                                        value: row[c.field], 
                                        format: c.formatter
                                    })}</a>
                                </td>
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>

        {props.onChangePage && <div>
            <DataTablePagination
                pageIndex={currentPage - 1}
                canPreviousPage={currentPage > 1}
                canNextPage={currentPage < totalPages}
                pageSize={props.pageSize || 50}
                setPageSize={(size: number) => {
                    props.onChangePage!(currentPage, size);
                }}
                pageCount={totalPages}
                nextPage={() => {
                    props.onChangePage!(currentPage + 1, props.pageSize || 50);
                }}
                previousPage={() => {
                    props.onChangePage!(currentPage - 1, props.pageSize || 50);
                }}
                gotoPage={(newPage) => {
                    props.onChangePage!(newPage + 1, props.pageSize || 50);
                }}
            />
        </div>}
    </div>
}

const getAllUpstreamNodes = (nodeId: string, graph: graphlib.Graph<string>): string[] => {
    const rv: string[] = [];
    const stack: string[] = [nodeId];
    while (stack.length > 0) {
        const nextNode = stack.pop() as string;
        rv.push(nextNode);
        const preds = graph.predecessors(nextNode);
        if (preds) {
            stack.push(...preds);
        }
    }
    return rv;
}

export const PivotTableVisualization = (props: VisualizationProps<TableVisualizationProps>) => {
    const columns = useMemo(() => {
        return props.params.options.columns.map(c => {
            return {
                header: c.headerName,
                key: c.field,
            }
        });
    }, [props.params.options.columns]);

    const dim1Column = useMemo(() => {
        return props.params.options.columns[0].field || '';
    }, [props.params.options.columns]);

    const onClickMetric = useCallback((colIdx: number, rowData: Record<string, any>) => {
        const dimensionFilters = [
            {
                dimension_id: props.dimensionIds[0],
                comparator: 'IN',
                value: [rowData[props.params.options.columns[0].field]],
            },
            {
                dimension_id: props.dimensionIds[1],
                comparator: 'IN',
                value: [props.params.options.columns[colIdx].field],
            }
        ];

        props.onDrillDown({
            metricIds: [props.measureIds[0]],
            dimensionIds: props.dimensionIds,
            dimensionFilters
        });
    }, [props.onDrillDown, props.dimensionIds, props.measureIds]);

    return <table className="table table-bordered table-compact">
        <thead className="bg-light">
            <tr>{props.params.options.columns.map(c => <th key={c.field}>{c.headerName}</th>)}</tr>
        </thead>
        <tbody>
            {props.params.options.data.map((row, idx) => {

                return <tr key={idx}>
                    {props.params.options.columns.map((c, idx) => {
                        if (idx == 0) {
                            return <th key={c.field}>{row[c.field]}</th>
                        }
                        return <td key={c.field}>
                            <a role="button" className="force-link" onClick={() => {
                                onClickMetric(idx, row);
                            }}>{row[c.field]}</a>
                            
                        </td>
                    })}
                </tr>
            })}
        </tbody>
    </table>
}

interface ApexVisualizationProps {
    options: ApexCharts.ApexOptions;
    render_type: string;
}

export const ApexVisualization = (props: VisualizationProps<ApexVisualizationProps>) => {
    const chartType = props.params.render_type == 'apex_bar' ? 'bar' : 'line';

    const handleDataPointSelection = useCallback((event: any, chartContext: any, config: any) => {
        if (!props.params.options.series) return;

        // @ts-ignore
        const series = props.params.options.series[config.seriesIndex].name;
        const xAxis = props.params.options.xaxis?.categories[config.dataPointIndex];

        const dimensionFilters = props.params.options.series.length > 1 ? [
            {
                dimension_id: props.dimensionIds[0],
                comparator: 'IN',
                value: [xAxis],
            },
            {
                dimension_id: props.dimensionIds[1],
                comparator: 'IN',
                value: [series],
            }
        ] : [
            {
                dimension_id: props.dimensionIds[0],
                comparator: 'IN',
                value: [xAxis],
            }
        ];

        props.onDrillDown({
            metricIds: [props.measureIds[0]],
            dimensionIds: props.dimensionIds,
            dimensionFilters
        });
    }, [props.onDrillDown, props.dimensionIds, props.measureIds]);

    const chartParams = {
        ...props.params.options,
        chart: {
            ...props.params.options.chart,
            events: {
                dataPointSelection: handleDataPointSelection
            }
        }
    }
    return <Chart
        options={chartParams}
        series={props.params.options.series}
        type={chartType}
        height="100%"
    />
}


interface DimensionFilterFormProps {
    dimensionFilters: AnalysisDimensionFilter[];
    onChange: (filters: AnalysisDimensionFilter[]) => void;
    dimensionOptions: Option[];
}

const DimensionFilterForm = (props: DimensionFilterFormProps) => {
    const addNewFilter = useCallback(() => {
        props.onChange([...props.dimensionFilters, {
            dimension_id: '',
            comparator: 'IN',
            value: '',
        }]);
    }, [props.onChange, props.dimensionFilters]);

    const handleDelete = useCallback((idx: number) => {
        props.onChange(produce(props.dimensionFilters, draft => {
            draft.splice(idx, 1);
        }));
    }, [props.onChange, props.dimensionFilters]);

    return <div>
        <table className="table table-bordered table-fixed table-centered">
            <thead className="bg-light">
                <tr>
                    <th style={{width:'25%'}}>Dimension</th>
                    <th style={{width:'25%'}}>Comparator</th>
                    <th style={{width: '40%'}}>Value</th>
                    <th style={{width:'10%'}} className="text-end">
                        <button className="icon-button" onClick={addNewFilter}>
                            <i className="mdi mdi-plus-thick"></i>
                        </button>
                    </th>
                </tr>
            </thead>
            <tbody>
                {props.dimensionFilters.map((f, idx) => (
                    <SingleDimensionFilter
                        displayType='table-row'
                        key={idx}
                        dimensionFilter={f}
                        onChange={(newFilter) => {
                            props.onChange(produce(props.dimensionFilters, draft => {
                                draft[idx] = newFilter;
                            }));
                        }}
                        dimensionOptions={props.dimensionOptions}
                        onDelete={() => handleDelete(idx)}
                        allowComparatorSelection
                    />
                ))}
            </tbody>
        </table>
    </div>
}

export class ReportBuilderState {

    constructor(
        graph: graphlib.Graph,
        allDimensions: ReportBuilderDimension[],
        allMeasures: ReportBuilderMeasure[],
        initialSelectedDimensions: string[],
        initialSelectedMeasures: string[],
    ) {
        this.allDimensions = allDimensions;
        this.allMeasures = allMeasures;
        this._availableDimensionIds = [];
        this._availableMeasureIds = [];
        this.selectedDimensionIds = initialSelectedDimensions;
        this.selectedMeasureIds = initialSelectedMeasures;
        this.graph = graph;

        const measuresById: {[key: string]: ReportBuilderMeasure} = {};
        this.allMeasures.forEach(m => {
            measuresById[m.id as string] = m;
        });
        this.measuresById = measuresById;

        const dimensionsById: {[key: string]: ReportBuilderDimension} = {};
        this.allDimensions.forEach(d => {
            dimensionsById[d.id as string] = d;
        });
        this.dimensionsById = dimensionsById;

        // Now create a graph of measure dependencies for calculations so we can handle multiple layers
        this.measureGraph = new graphlib.Graph<string>({
            directed: true,
            multigraph: true,
        });

        this.allMeasures.forEach(m => {
            this.measureGraph.setNode(m.id as string, m.name);
            if (m.is_calculation && m.parent_measure_ids) {
                m.parent_measure_ids.forEach(parentId => {
                    this.measureGraph.setEdge(parentId, m.id as string);
                });
            }
        });

        
        this.updateAvailableDimensionIds();
        this.updateAvailableMeasureIds();
    }

    protected measuresById: {[key: string]: ReportBuilderMeasure};
    protected dimensionsById: {[key: string]: ReportBuilderDimension};

    public allDimensions: ReportBuilderDimension[] = [];
    public allMeasures: ReportBuilderMeasure[] = [];
    public selectedDimensionIds: string[] = [];
    public selectedMeasureIds: string[] = [];
    protected graph: graphlib.Graph;
    protected measureGraph: graphlib.Graph<string>;
    

    protected _availableDimensionIds: string[] = [];
    protected _availableMeasureIds: string[] = [];
    public get availableDimensionIds() {
        return this._availableDimensionIds;
    }
    public get availableMeasureIds() {
        return this._availableMeasureIds;
    }

    public hasMissingDeps(measure: ReportBuilderMeasure): boolean {
        if (!measure.is_calculation || !measure.parent_measure_ids) {
            return false;
        }
        
        return this.selectedMeasureIds.includes(measure.id as string) && 
               measure.parent_measure_ids.some(parentId => 
                   !this.selectedMeasureIds.includes(parentId));
    }

    public getMissingDeps(measure: ReportBuilderMeasure): string[] {
        if (!measure.is_calculation || !measure.parent_measure_ids) {
            return [];
        }
        return measure.parent_measure_ids.filter(id => 
            !this.selectedMeasureIds.includes(id));
    }

    public getMeasureNameById(id: string): string {
        return this.measuresById[id]?.name || id;
    }

    public hasAnyMissingDeps(): boolean {
        return this.allMeasures.some(measure => this.hasMissingDeps(measure));
    }

    public getMissingDepsMessage(measure: ReportBuilderMeasure): string {
        const missingDeps = this.getMissingDeps(measure);
        if (!measure.parent_measure_ids || missingDeps.length === 0) {
            return '';
        }

        return `This measure requires ${measure.parent_measure_ids.map(id => 
            this.getMeasureNameById(id)).join(' and ')} in order to be calculated, but is missing ${
            missingDeps.map(id => this.getMeasureNameById(id)).join(', ')}.`;
    }

    public updateAvailableDimensionIds() {
        console.log('Updating available dimension ID')
        if (this.selectedMeasureIds.length == 0) {
            return [];
        }

        const currentlySelectedNodeIds = this.allDimensions.filter(d => this.selectedDimensionIds.includes(d.id as string)).map(d => d.pipeline_node_id) || [];
        
        
        let requiredDimensionIds: string[] = [];

        /**
         * Get a list of the pipeline node IDs that are connected to EVERY selected measure
         */
        const measureIdsToConsiderForDimensionsList = [...this.selectedMeasureIds];
        this.selectedMeasureIds.forEach((mid, idx) => {
            const measure = this.measuresById[mid];
            if (measure?.is_calculation) {
                const allUpstream = getAllUpstreamNodes(measure.id as string, this.measureGraph);
                // Now concatenate the upstream nodes to the list of measures to consider
                measureIdsToConsiderForDimensionsList.push(...allUpstream);
            }
        })

        // Ensure the list is unique so we don't double-process
        const uniqueMeasureIdsToConsider = new Set(measureIdsToConsiderForDimensionsList);
        if (uniqueMeasureIdsToConsider.size > 0) {
            let startedTree = false;
            uniqueMeasureIdsToConsider.forEach((mid, idx) => {
                const measure = this.measuresById[mid];
                if (!measure) {
                    return;
                }

                if (measure?.is_calculation) {
                    return;
                }
                
                // Possible edge case where the measure doesn't yet have a pipeline node ID set
                const startWith: string[] = [];
                if (measure.pipeline_node_id) {
                    startWith.push(measure.pipeline_node_id as string);
                }

                /**
                 * Here we're basically building a list of pipeline node IDs that are connected to
                 * every single selected measure. If we haven't selected a measure yet, (startedTree == false)
                 * then we just start with the selected measure's pipeline node ID and all of that measure's join
                 * trees.
                 * 
                 * If we have already started the tree, then we filter the list of required dimension IDs to only
                 * those that are also connected to every subsequent measure.
                 */
                if (!startedTree) {
                    requiredDimensionIds = startWith.concat(measure?.dimension_join_trees?.filter(d => !d.ignore).map(d => d.pipeline_node_id) || []);
                    startedTree = true;
                } else {
                    requiredDimensionIds = startWith.concat(requiredDimensionIds.filter(d => measure?.dimension_join_trees?.filter(d => !d.ignore).map(d => d.pipeline_node_id).includes(d)));
                }
            });
        }

        /**
         * Now we can select the available dimension IDs. 
         */
        const uniqueNodeIds = new Set(currentlySelectedNodeIds);
        const MAX_NODES = 5;
        
        this._availableDimensionIds = this.allDimensions.filter(d => {
            // if (this.selectedDimensionIds.includes(d.id as string)) {
            //     return true;
            // }

            if (!d.pipeline_node_id) {
                return false;
            }

            if (!requiredDimensionIds.includes(d.pipeline_node_id)) {
                return false;
            }

            // If they've already hit the max, then only allow dimensions in the already selected nodes
            if (uniqueNodeIds.size >= MAX_NODES) {
                return uniqueNodeIds.has(d.pipeline_node_id);
            }
            
            if (currentlySelectedNodeIds.length == 0) {
                return true;
            }

            return true;
        }).map(d => d.id as string);
        
    }

    protected updateAvailableMeasureIds() {
        let requiredDimensionIds: string[] = [];
        /**
         * Get a list of the pipeline node IDs that are connected to EVERY selected measure
         */
        this.selectedMeasureIds.forEach((mid, idx) => {
            const measure = this.measuresById[mid];
            if (idx == 0) {
                requiredDimensionIds = measure?.dimension_join_trees?.map(d => d.pipeline_node_id) || [];
            } else {
                requiredDimensionIds = requiredDimensionIds.filter(d => measure?.dimension_join_trees?.map(d => d.pipeline_node_id).includes(d));
            }
        });
        const requiredPipelineNodeIdsFromCurrentDimensions = this.selectedDimensionIds.filter(did => this.dimensionsById.hasOwnProperty(did)).map(did => this.dimensionsById[did].pipeline_node_id) || [];

        requiredDimensionIds = requiredDimensionIds?.filter(did => requiredPipelineNodeIdsFromCurrentDimensions.includes(did));

        this._availableMeasureIds = this.allMeasures.filter(m => {
            if (this.selectedMeasureIds.includes(m.id as string)) {
                return true;
            }
            
            if (m.is_calculation) {
                if (!m.parent_measure_ids) {
                    return true;
                }
                // return m.parent_measure_ids.every(pmid => 
                //     this.selectedMeasureIds.includes(pmid));
                // Now we allow them to select a calculated measure because we automatically include its dependencies in the backend.
                return true;
            }

            if (!m.dimension_join_trees) {
                return false;
            }

            if (requiredDimensionIds.length == 0) {
                return true;
            }
            
            const allConnectedPipelineNodeIdsForThisMeasure = m.dimension_join_trees
                .map(d => d.pipeline_node_id);
            return requiredDimensionIds.every(r => 
                allConnectedPipelineNodeIdsForThisMeasure.includes(r));
        }).map(m => m.id as string);
    }
}

const DataOption = styled.div<{disabled?: boolean, used?: boolean, noHover?: boolean}>`
font-family: "Poppins";
&:last-child {
    margin-bottom: 0;
}

font-weight: 600;

margin-bottom: .25rem;

.inner {
border: solid 1px var(--ct-border-color);
border-radius: 4px;

padding: .25rem;
}

${props => !props.noHover && css`
    &:hover .inner {
        background-color: var(--ct-light);
        cursor: move;
    }
`}


${props => props.disabled && css`
    opacity: 0.5;
    pointer-events: none;
`}
`

const DropZone = styled.div`
border-radius: 6px;
border: dashed 2px var(--ct-border-color);
min-height: 40px;
padding: .25rem;
div.placeholder {
    height: 50px;
    line-height: 50px;
    color: var(--ct-border-color);
    text-align: center;
    font-family: "Poppins";


}
`




const VizOptionButton = styled.button`
border-radius: 4px;
border: 1px solid var(--ct-border-color);
`

export const OptionGrid = styled.div`
display: grid;
gap: 10px;
grid-template-columns: 1fr 1fr;

button.active {
    background-color: var(--pliable-blue-bg);
}
`

const ChatWindow = styled.div`
    padding: 3rem;
    background: var(--ct-light);

    .message {
        margin-bottom: 1rem;

        &:last-child {
            margin-bottom: 0;
        }

        .message-from {
            font-weight: bold;
            color: var(--ct-body-color);
        }
            .message-content {
                padding: 0.5rem 0.75rem;
                background-color: white;
                border-radius: 4px;
            }
    }
`

interface AnalysisDisplayProps {
    analysis: Analysis;
    removeSort: (entityType: string, entityId: string) => void;
    toggleSort: (entityType: string, entityId: string) => void;
    onDrillDown: (params: DrillDownParams) => void;
    showEmptyState?: boolean;
    showLastUpdated?: boolean;
    visualizationConfig?: AnalysisVisualizationConfig;
}
export const AnalysisDisplay = (props: AnalysisDisplayProps) => {
    const [visualizationConfig, setVisualizationConfig] = useState<AnalysisVisualizationConfig | undefined>(props.visualizationConfig);

    const [downloadNodeId, setDownloadNodeId] = useState('');
    const backingNode = usePipelineNode(downloadNodeId);

    const [running, setRunning] = useState(false);
    const [loadingViz, setLoadingViz] = useState(false);
    const [vizError, setVizError] = useState('');
    const [buildError, setBuildError] = useState('');
    const [loadingMessage, setLoadingMessage] = useState('');

    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [showConfetti, setShowConfetti] = useState(false);
    const [activeOrchestration, setActiveOrchestration] = useState<BuildOrchestration|undefined>(undefined);
    const [isPrepped, setIsPrepped] = useState(false);

    const loadVisualizationConfig = useCallback(async (shouldShowConfetti: boolean = true, suppressErrors: boolean = false) => {
        setLoadingViz(true);
        setVizError('');
        setVisualizationConfig(undefined);
        setDownloadNodeId('');
        try {
            // Make sure there aren't any null IDs going to the back end otherwise it won't properly pass the dimension_ids into the analysis.
            const cleanedAnalysis = produce(props.analysis, draft => {
                draft.measure_ids = draft.measure_ids.filter(m => !!m);
                draft.dimension_ids = draft.dimension_ids.filter(m => !!m);
            });
            const config = await AnalysisORM.getVisualizationConfig({
                analysis: cleanedAnalysis,
                sortOrder: props.analysis.default_sort_order || [],
                page: pageNum,
                perPage: pageSize,
            });
            setVisualizationConfig(config.record);
            setDownloadNodeId(config.node_id);
            if (shouldShowConfetti) {
                setShowConfetti(true);
                setTimeout(() => {
                    setShowConfetti(false);
                }, 2000);
            }

            // Tell other parts of the app that we've run at least one analysis.
            const currentTimes = localStorage.getItem('timesRanAnalysis');
            const parsed = currentTimes ? parseInt(currentTimes, 10) : 0; 
            
    
            localStorage.setItem('timesRanAnalysis', ((parsed || 0) + 1).toString());

            // Set isPrepped to true here just in case we're jumping around navigating. If we loaded it successfully we are prepped
            // setIsPrepped(true);
            
        } catch (err) {
            if (!suppressErrors) {
                setVizError(getErrorMessage(err));
            }
        } finally {
            setLoadingViz(false);
        }
    }, [pageNum, props.analysis, pageSize]);
    const runAnalysis = useCallback(async () => {
        setVizError('');
        setBuildError('');
        setLoadingMessage(getRandomLoadingMessage());
        setRunning(true);
        setIsPrepped(false);
        setVisualizationConfig(undefined);
        try {
            const orchestration = await AnalysisORM.prepAnalyses([props.analysis], []);
            setActiveOrchestration(orchestration);
        } catch (err) {
            setBuildError(getErrorMessage(err));
            setRunning(false);
        }
    }, [props.analysis]);

    const checkOrchestration = useCallback(async () => {
        if (!activeOrchestration) {
          
            return;
        }

        if (!['ERROR', 'COMPLETE', 'IN_REVIEW', 'CANCELLED'].includes(activeOrchestration.status)) {
            setTimeout(async () => {
                // Keep polling, which should then re-run this effect, and so on.
                const updatedOrch = await BuildOrchestrationORM.findById(activeOrchestration.id as string);
                setActiveOrchestration(updatedOrch);
            }, 1000);
            
        } else if (activeOrchestration.status == 'COMPLETE') {
            // setShowConfetti(true);
            // setTimeout(() => {
            //     setShowConfetti(false);
            // }, 1000);
            // invalidateEverything();
            setRunning(false);
            invalidatePipelineNodes();
            loadVisualizationConfig(true, false);
            
        } else if (activeOrchestration.status == 'ERROR') {
            setBuildError(getErrorMessage(activeOrchestration.error));
            setRunning(false);
            // toast('danger', 'Error', getErrorMessage(activeOrchestration.error));
        }
    }, [activeOrchestration, loadVisualizationConfig]);

    useEffect(() => {
        if (activeOrchestration) {
            checkOrchestration();
        }
    }, [activeOrchestration]);
    
    

    useEffect(() => {
        if (props.visualizationConfig) {
            console.log('Using passed-in visualization config');
            setVisualizationConfig(props.visualizationConfig);
            setRunning(false);
            setLoadingViz(false);
            setBuildError('');
            setVizError('');
        } else {
            runAnalysis();
        }
    }, [props.analysis, props.visualizationConfig]);

    return <div className="h-100 d-flex flex-column">
        <div className="d-flex center-vertically mb-1">
            {props.analysis.visualization_type == 'TABLE' && !!visualizationConfig && <SortConfiguration
                sortOrder={props.analysis.default_sort_order || []}
                params={visualizationConfig}
                measureIds={props.analysis.measure_ids}
                dimensionIds={props.analysis.dimension_ids}
                onRemoveSort={props.removeSort}
            />}
            <div className="flex-1">

            </div>
            {!!backingNode.data && props.showLastUpdated && <div className="text-muted text-end font-13">
                <i className="mdi mdi-clock"></i> Data last updated {timeAgo(backingNode.data.last_build_completed)}. Click "Refresh" to get the latest data.  
            </div>}
        </div>
        
        <div className="flex-1">
            {!running && !loadingViz && !visualizationConfig && !vizError && !buildError && <div>
                {props.showEmptyState && <div className="empty-state">
                    <div className="text-center">
                        <h3>Chart Builder.</h3>
                        <p>Configure your chart using the form at the right side of the screen, and hit "Go" when you're ready.</p>
                        
                        
                    </div>
                </div>}
                
                    
            </div>}
            {(running || loadingViz) && <div>
                <div style={{margin: 'auto', width: '150px'}}>
                    <PliableLoader size="lg"></PliableLoader>
                </div>
                <div className="text-center">
                    <h3 className="mb-0 fw-bold">{loadingMessage}</h3>

                </div>
                
                
            </div>}
            {!running && buildError && <>
                <Danger>
                    {buildError}
                </Danger>
            </>}
            {!loadingViz && vizError && <>
                {vizError.toLowerCase().includes('no data') ? (
                    <Warning>
                        {vizError}
                    </Warning>
                ) : (
                    <Danger>
                        {vizError}
                    </Danger>
                )}
            </>}
            {!running && !!visualizationConfig && props.analysis.visualization_type == 'SINGLE_VALUE' && 
                <SingleValueVisualization 
                    measureIds={props.analysis.measure_ids} 
                    dimensionIds={props.analysis.dimension_ids} 
                    sortOrder={props.analysis.default_sort_order || []} 
                    onToggleSort={props.toggleSort} 
                    params={visualizationConfig} 
                    onDrillDown={props.onDrillDown}
                />
            }
            {!running && !!visualizationConfig && props.analysis.visualization_type == 'TABLE' && 
                <TableVisualization 
                    measureIds={props.analysis.measure_ids} 
                    dimensionIds={props.analysis.dimension_ids} 
                    sortOrder={props.analysis.default_sort_order || []} 
                    onToggleSort={props.toggleSort}
                    onRemoveSort={props.removeSort}
                    params={visualizationConfig} 
                    onDrillDown={props.onDrillDown}
                    onChangePage={(newPage, pageSize) => {
                        console.log('Changed page', newPage, pageSize);
                        setPageNum(newPage);
                        setPageSize(pageSize);
                    }}
                    pageSize={pageSize}
                    currentPage={pageNum}
                />
            }
            {!running && !!visualizationConfig && props.analysis.visualization_type == 'PIVOT_TABLE' && <PivotTableVisualization 
                sortOrder={props.analysis.default_sort_order || []} 
                measureIds={props.analysis.measure_ids} 
                dimensionIds={props.analysis.dimension_ids} 
                onToggleSort={props.toggleSort} 
                params={visualizationConfig} 
                onDrillDown={props.onDrillDown}
            />}
            {!running && !!visualizationConfig && props.analysis.visualization_type == 'PREMIUM_LTV_PIVOT_TABLE' && <LTVPivotTableVisualization 
                data={visualizationConfig}
                analysis={props.analysis}
                onDrillDown={props.onDrillDown}
            />}
            {!running && !!visualizationConfig && props.analysis.visualization_type == 'PREMIUM_CUSTOMER_ACQUISITION' && visualizationConfig.render_type === 'table' &&
                <TableVisualization 
                    measureIds={props.analysis.measure_ids} 
                    dimensionIds={props.analysis.dimension_ids} 
                    sortOrder={props.analysis.default_sort_order || []} 
                    onToggleSort={props.toggleSort}
                    onRemoveSort={props.removeSort}
                    params={visualizationConfig} 
                    onDrillDown={props.onDrillDown}
                    onChangePage={(newPage, newPageSize) => {
                        setPageNum(newPage);
                        setPageSize(newPageSize);
                    }}
                    pageSize={pageSize}
                    currentPage={pageNum}
                />
            }
            {!running && !!visualizationConfig && props.analysis.visualization_type == 'PREMIUM_CUSTOMER_ACQUISITION' && visualizationConfig.render_type !== 'table' && <CustomerAcquisitionVisualization 
                data={visualizationConfig}
                analysis={props.analysis}
                onDrillDown={props.onDrillDown}
            />}
            {!running && !!visualizationConfig && props.analysis.visualization_type == 'BAR' && <ApexVisualization 
                measureIds={props.analysis.measure_ids} 
                dimensionIds={props.analysis.dimension_ids} 
                sortOrder={props.analysis.default_sort_order || []} 
                onToggleSort={props.toggleSort} 
                params={visualizationConfig} 
                onDrillDown={props.onDrillDown}
            />}
            {!running && !!visualizationConfig && props.analysis.visualization_type == 'LINE' && <ApexVisualization 
                measureIds={props.analysis.measure_ids} 
                dimensionIds={props.analysis.dimension_ids} 
                sortOrder={props.analysis.default_sort_order || []} 
                onToggleSort={props.toggleSort} 
                params={visualizationConfig} 
                onDrillDown={props.onDrillDown}
            />}
        </div>
    </div>
}
